import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Plus, Users, Crown, ArrowRight, Trophy, Clock, Share2, Heart, HandHelping } from "lucide-react";

export default function Challenges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [coopItems, setCoopItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) {
      setLoading(false);
      return;
    }
    // Coop challenges are open to everyone (free + premium) to drive engagement.
    api.get("/coop-challenges/mine/list").then((r) => setCoopItems(r.data)).catch(() => {});
    // Classic challenges remain Premium-only
    if (user.plan === "premium") {
      api.get("/challenges/mine").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const isPremium = user?.plan === "premium";

  return (
    <div className="min-h-screen paper-bg">
      <Navbar variant="app" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-mustard text-navy font-bold px-3 py-1 rounded-full text-sm mb-3">
              <Users className="w-4 h-4" /> Défi Famille
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold text-navy mb-2">
              Vos défis en cours
            </h1>
            <p className="text-lg text-navy/70 max-w-2xl">
              Créez un quiz, partagez le lien à vos petits-enfants par message ou email,
              et comparez les scores en direct.
            </p>
          </div>
          {isPremium ? (
            <Link
              to="/app/challenges/new"
              data-testid="challenges-new-btn"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white font-bold px-6 py-4 rounded-full shadow-warm transition self-start md:self-auto"
            >
              <Plus className="w-5 h-5" /> Nouveau défi
            </Link>
          ) : (
            <Link
              to="/app/pricing"
              data-testid="challenges-upgrade-cta"
              className="inline-flex items-center gap-2 bg-mustard hover:bg-mustard-dark text-navy font-bold px-6 py-4 rounded-full transition"
            >
              <Crown className="w-5 h-5" /> Débloquer avec Premium
            </Link>
          )}
        </div>

        {!isPremium && (
          <div className="bg-mustard/25 border-2 border-mustard-dark rounded-3xl p-6 md:p-8 mb-8" data-testid="challenges-locked">
            <div className="flex flex-col md:flex-row items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-mustard flex items-center justify-center shrink-0">
                <Crown className="w-7 h-7 text-navy" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-2xl font-bold text-navy mb-2">Défi Classique réservé aux Premium</h2>
                <p className="text-navy/80 text-lg leading-relaxed">
                  Le Défi Famille Classique (jeu en équipe à distance) est inclus dans Premium.
                  Le <strong>Mode Coopératif</strong> ci-dessous est <strong>accessible à tous</strong> — essayez-le !
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ============ COOP HERO CARD ============ */}
        <div className="bg-gradient-to-br from-navy via-navy to-bordeaux text-cream rounded-[28px] p-6 md:p-8 mb-8 relative overflow-hidden" data-testid="coop-hero">
          <div className="absolute top-4 right-4 bg-mustard text-navy text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            Nouveau
          </div>
          <div className="flex items-start gap-4 mb-5">
            <div className="w-14 h-14 rounded-2xl bg-mustard flex items-center justify-center shrink-0">
              <Heart className="w-7 h-7 text-bordeaux fill-current" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/15 text-cream font-bold px-2.5 py-1 rounded-full text-xs mb-2">
                <HandHelping className="w-3.5 h-3.5" /> Mode Coopératif
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-2 leading-tight">
                Senior + Jeune <span className="text-mustard italic">= une équipe</span>
              </h2>
              <p className="text-cream/85 text-lg leading-relaxed mb-4">
                Jouez à 2 sur le même téléphone. Si l&apos;un bloque, il <strong>passe le téléphone</strong> à l&apos;autre — la complicité avant la performance.
                100 points en solo, 50 points avec un coup de main.
              </p>
            </div>
          </div>
          <Link
            to="/app/coop/new"
            data-testid="coop-new-btn"
            className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white font-bold px-6 py-4 rounded-full text-lg shadow-warm transition"
          >
            <Plus className="w-5 h-5" /> Lancer un défi coopératif <ArrowRight className="w-5 h-5" />
          </Link>

          {coopItems.length > 0 && (
            <div className="mt-6 pt-6 border-t-2 border-white/15">
              <div className="text-xs font-bold uppercase tracking-wider text-cream/70 mb-3">Reprendre un défi coopératif</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {coopItems.slice(0, 4).map((c) => (
                  <Link
                    key={c.token}
                    to={`/app/coop/${c.token}`}
                    data-testid={`coop-resume-${c.token}`}
                    className="bg-white/10 hover:bg-white/20 rounded-2xl p-3 transition flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-bold truncate">{c.team_name}</div>
                      <div className="text-xs text-cream/70">
                        {c.status === "completed"
                          ? `Terminé · ${c.stats_coop?.total_xp || 0} points`
                          : `Q${(c.current_index || 0) + 1}/${c.total_questions} · ${c.stats_coop?.total_xp || 0} points`}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 shrink-0 text-cream/70" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============ CLASSIC SECTION HEADER — visible pour tous ============ */}
        <div className="mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-bold text-navy flex items-center gap-2">
              <Users className="w-6 h-6 text-terracotta" /> Défi Classique
            </h2>
            {!isPremium && (
              <span className="inline-flex items-center gap-1 bg-mustard/40 text-navy text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
          </div>
          <p className="text-sm text-navy/60">Quiz partagé par lien — chacun joue de son côté et compare les scores.</p>
        </div>

        {loading ? (
          <div className="text-navy/60 text-lg">Chargement...</div>
        ) : !isPremium ? (
          // Non-premium : aperçu de 2 cards de démo verrouillées + CTA d'upgrade
          <div className="grid md:grid-cols-2 gap-6" data-testid="challenges-demo-locked">
            {[
              { title: "Chansons françaises", participants: 3, mascot: "🎵" },
              { title: "Cinéma d'antan", participants: 5, mascot: "🎬" },
            ].map((demo, i) => (
              <div
                key={i}
                className="relative bg-white border-2 border-cream-dark rounded-[24px] p-6 opacity-70 pointer-events-none"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-cream border-2 border-cream-dark flex items-center justify-center text-3xl shrink-0">
                    {demo.mascot}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-bold text-navy">{demo.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-navy/60 mt-1">
                      <span>10 questions</span>
                      <span>·</span>
                      <span>Aperçu</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-cream rounded-2xl px-4 py-3 mb-4">
                  <div className="flex items-center gap-2 text-navy">
                    <Users className="w-5 h-5 text-terracotta" />
                    <span className="font-bold">{demo.participants} participants</span>
                  </div>
                  <div className="flex items-center gap-1 text-navy">
                    <Trophy className="w-5 h-5 text-mustard-dark" />
                    <span className="font-bold">—</span>
                  </div>
                </div>
                <div className="w-full inline-flex items-center justify-center gap-2 bg-navy/40 text-white font-bold px-5 py-3 rounded-full">
                  <Share2 className="w-4 h-4" /> Voir & partager
                </div>
              </div>
            ))}

            {/* Overlay CTA sous les 2 cards démo */}
            <div className="md:col-span-2 -mt-2 flex items-center justify-center">
              <Link
                to="/app/pricing"
                data-testid="challenges-demo-upgrade"
                className="inline-flex items-center gap-2 bg-mustard hover:bg-mustard-dark text-navy font-bold px-6 py-4 rounded-full shadow-warm transition"
              >
                <Crown className="w-5 h-5" /> Débloquer les Défis avec Premium <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border-2 border-cream-dark rounded-[28px] p-10 text-center" data-testid="challenges-empty">
            <Users className="w-14 h-14 mx-auto text-terracotta/60 mb-4" strokeWidth={2} />
            <h3 className="font-display text-2xl font-bold text-navy mb-2">Aucun défi pour le moment</h3>
            <p className="text-navy/70 mb-6">Lancez votre premier défi à votre famille en quelques secondes.</p>
            {isPremium && (
              <Link
                to="/app/challenges/new"
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white font-bold px-6 py-3 rounded-full shadow-warm"
              >
                <Plus className="w-5 h-5" /> Créer mon premier défi
              </Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((c, idx) => (
              <motion.div
                key={c.token}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="bg-white border-2 border-cream-dark rounded-[24px] p-6 hover:border-terracotta hover:-translate-y-0.5 transition"
                data-testid={`challenge-card-${c.token}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  {c.category_mascot_image && (
                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-cream border-2 border-cream-dark shrink-0">
                      <img src={`${BACKEND_URL}${c.category_mascot_image}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-bold text-navy truncate">{c.category_title || c.category_id}</h3>
                    <div className="flex items-center gap-3 text-sm text-navy/60 mt-1">
                      <span>{c.total_questions} questions</span>
                      <span>·</span>
                      <span>{new Date(c.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-cream rounded-2xl px-4 py-3 mb-4">
                  <div className="flex items-center gap-2 text-navy">
                    <Users className="w-5 h-5 text-terracotta" />
                    <span className="font-bold">
                      {c.participants?.length || 0} participant{(c.participants?.length || 0) > 1 ? "s" : ""}
                    </span>
                  </div>
                  {c.participants?.length > 0 && (
                    <div className="flex items-center gap-1 text-navy">
                      <Trophy className="w-5 h-5 text-mustard-dark" />
                      <span className="font-bold">{c.participants[0].name}</span>
                      <span className="text-sm">({c.participants[0].score}/{c.participants[0].total})</span>
                    </div>
                  )}
                </div>

                <Link
                  to={`/app/challenges/${c.token}`}
                  data-testid={`challenge-open-${c.token}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold px-5 py-3 rounded-full transition"
                >
                  <Share2 className="w-4 h-4" /> Voir & partager <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
